import { useState } from "react"
import { Section, CodeBlock, InfoBox, FileTree, FileExplanationPanel } from "@/components"
import { reactShadcnProjectStructure } from "@/data/project-structures"
import { reactShadcnFileExplanations, type FileExplanation } from "@/data/file-explanations"

export function ReactTailwindArchitecturePage() {
  const [selectedFile, setSelectedFile] = useState<FileExplanation | null>(null)

  const handleFileClick = (filePath: string) => {
    const fileInfo = reactShadcnFileExplanations[filePath]
    if (fileInfo) {
      setSelectedFile(fileInfo)
    }
  }

  return (
    <>
      <div className="space-y-10">
        <div className="space-y-4">
          <h1 className="text-4xl font-bold">Architecture</h1>
          <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
            Understand the clean architecture pattern used in this template. The codebase follows 
            a modular structure with clear separation of concerns for maintainability and scalability.
            Click on any file below to see detailed explanations.
          </p>
        </div>

        <InfoBox type="info" title="Interactive File Explorer">
          Click any file in the tree below to view its purpose, key features, and related files. 
          This template uses pure Tailwind CSS with custom components.
        </InfoBox>

        <Section title="Project Structure" description="Interactive file tree">
          <FileTree data={reactShadcnProjectStructure} onFileClick={handleFileClick} />
        </Section>

        <Section title="Architectural Pattern" description="Layout → Page → View">
          <div className="space-y-6">
            <p className="text-muted-foreground leading-relaxed">
              The template uses a three-layer architecture that separates concerns and makes the codebase easy to navigate:
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-card border rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg">Layout</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Provides the overall page structure like sidebars, headers, and footers. 
                  Layouts are reusable across multiple pages.
                </p>
                <code className="text-xs bg-muted px-2 py-1 rounded block">src/layouts/</code>
              </div>
              
              <div className="bg-card border rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg">Page</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Thin layer that connects routes to views. Handles layout selection and 
                  renders the appropriate view component.
                </p>
                <code className="text-xs bg-muted px-2 py-1 rounded block">src/pages/</code>
              </div>
              
              <div className="bg-card border rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg">View</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Contains the actual UI and business logic. Manages state, data fetching, 
                  and user interactions for each page.
                </p>
                <code className="text-xs bg-muted px-2 py-1 rounded block">src/views/</code>
              </div>
            </div>

            <InfoBox type="info" title="Example Flow">
              Route <code className="bg-muted px-2 py-1 rounded">/dashboard</code> → 
              Page component selects <strong>AppLayout</strong> → 
              Renders <strong>DashboardView</strong> inside layout
            </InfoBox>
          </div>
        </Section>

        <Section title="Key Modules" description="Main architectural components">
          <div className="space-y-6">
            <ModuleCard
              title="Components"
              description="Custom components built with pure Tailwind CSS utility classes"
              features={[
                "Custom UI components (Button, Card, Input, etc.)",
                "Dashboard components (MetricCard, DataTable, ChartCard)",
                "Chat components (ChatList, ChatMessage, ChatInput)",
                "Common components (ErrorBoundary, EmojiPicker, Modal)",
                "Layout components (Sidebar, Header)",
              ]}
            />

            <ModuleCard
              title="Pages & Views"
              description="Follows the Layout → Page → View pattern for clean separation"
              features={[
                "Pages handle routing and layout selection",
                "Views contain business logic and state",
                "Layouts provide reusable page structure",
                "Pages: Dashboard, Chat, Users, Analytics, Settings, Auth",
              ]}
            />

            <ModuleCard
              title="Services"
              description="API communication layer with Axios"
              features={[
                "Centralized Axios client with interceptors",
                "Auth service for authentication",
                "User service for user management",
                "Product service (example CRUD operations)",
                "Request/response interceptors for tokens",
              ]}
            />

            <ModuleCard
              title="State Management"
              description="Zustand stores for global state"
              features={[
                "AuthStore for user and token management",
                "ThemeStore for light/dark mode",
                "SidebarStore for sidebar state",
                "ConfirmationStore for global dialogs",
                "LoaderStore for loading states",
              ]}
            />

            <ModuleCard
              title="Hooks & Utils"
              description="Custom hooks and utility functions"
              features={[
                "useAuth for authentication state",
                "useTheme for theme management",
                "useLocalStorage for persistence",
                "useProducts for data fetching",
                "Format and validation utilities",
              ]}
            />
          </div>
        </Section>

        <Section title="Code Example" description="Layout → Page → View pattern">
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">1. Layout (AppLayout.tsx)</h3>
              <CodeBlock
                code={`import { Outlet } from "react-router-dom"
import { Sidebar } from "@/components/layout/Sidebar"
import { Header } from "@/components/layout/Header"

export function AppLayout() {
  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet /> {/* Child routes render here */}
        </main>
      </div>
    </div>
  )
}`}
                language="typescript"
                filename="src/layouts/AppLayout.tsx"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">2. Page (DashboardPage.tsx)</h3>
              <CodeBlock
                code={`import { DashboardView } from "@/views/DashboardView"

export function DashboardPage() {
  // Minimal logic - just renders the view
  return <DashboardView />
}

export default DashboardPage`}
                language="typescript"
                filename="src/pages/DashboardPage.tsx"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">3. View (DashboardView.tsx)</h3>
              <CodeBlock
                code={`import { useEffect, useState } from "react"
import { MetricCard } from "@/components/dashboard/MetricCard"
import { LoadingSpinner } from "@/components/common/LoadingSpinner"
import { dashboardService } from "@/services/dashboard.service"

export function DashboardView() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    dashboardService.getStats()
      .then(setStats)
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
        Dashboard
      </h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard title="Total Users" value={stats.users} trend="up" />
        <MetricCard title="Revenue" value={\`$\${stats.revenue}\`} trend="up" />
        <MetricCard title="Active Sessions" value={stats.sessions} />
      </div>
    </div>
  )
}`}
                language="typescript"
                filename="src/views/DashboardView.tsx"
              />
            </div>
          </div>
        </Section>

        <Section title="Tailwind CSS Approach" description="Utility-first styling">
          <div className="space-y-6">
            <p className="text-muted-foreground leading-relaxed">
              This template uses pure Tailwind CSS utility classes for all styling. Components are built 
              from scratch without external UI libraries, giving you complete control over design and behavior.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-card border rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg">Maximum Flexibility</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  No pre-built components means no constraints. Style exactly what you need with Tailwind's 
                  utility classes.
                </p>
              </div>
              
              <div className="bg-card border rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg">Minimal Bundle Size</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  PurgeCSS removes unused styles in production, resulting in incredibly small CSS files.
                </p>
              </div>
            </div>

            <CodeBlock
              code={`// Example: Custom Button component with Tailwind
interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  children: React.ReactNode
}

export function Button({ variant = 'primary', size = 'md', children }: ButtonProps) {
  const baseClasses = 'rounded-lg font-medium transition-colors'
  
  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300',
    outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
  }
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  }
  
  return (
    <button 
      className={\`\${baseClasses} \${variantClasses[variant]} \${sizeClasses[size]}\`}
    >
      {children}
    </button>
  )
}`}
              language="typescript"
              filename="src/components/common/Button.tsx"
            />
          </div>
        </Section>

        <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
          <h2 className="text-2xl font-bold">Best Practices</h2>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Keep views focused on a single responsibility</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Move reusable logic to custom hooks</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Use the service layer for all API calls</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Define TypeScript types for all data structures</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Keep components small and composable</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Use Tailwind's @apply for repeated utility combinations</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Leverage Tailwind's JIT compiler for rapid development</span>
            </li>
          </ul>
        </div>
      </div>

      <FileExplanationPanel 
        file={selectedFile} 
        onClose={() => setSelectedFile(null)} 
      />
    </>
  )
}

function ModuleCard({
  title,
  description,
  features
}: {
  title: string
  description: string
  features: string[]
}) {
  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      <div>
        <h3 className="font-semibold text-lg mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
      </div>
      <div>
        <h4 className="text-sm font-semibold mb-3">Key Features:</h4>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex gap-2 text-sm text-muted-foreground">
              <span className="text-primary">→</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
